import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'

import SortableTable from '../CommonComponents/SortableTableComponent'
import TableCell from '../CommonComponents/TableCell'
import DupTargetModal from './DupTargetModal'

import {
  parseAsinTarget,
  tableSorter,
  calcDerivedMetrics,
  getExportValueForColumn,
} from '../../services/helper'

import { bulkBidColumnList } from '../../utils/defaultValues'

const initialColumns = [
  { key: 'target_text', name: 'ASIN', className: 'col-target' },
]

const DupTargetTable = ({ onChangeDate }) => {
  const currencyRate = useSelector(state => state.header.currencyRate)
  const currencySign = useSelector(state => state.header.currencySign)
  const campaignTableColumns = useSelector(state => state.pageGlobal.campaignTableColumns)
  const filterValues = useSelector(state => state.pageGlobal.filterValues)
  const campaignsWithHistory = useSelector(state => state.campaign.campaignsWithHistory)
  const findDupsData = useSelector(state => state.bulkEngine.findDupsData)

  const [targets, setTargets] = useState([])
  const [selectedTarget, setSelectedTarget] = useState(null)

  useEffect(() => {
    const campaignsById = {}
    campaignsWithHistory.forEach((campaign) => {
      campaignsById[campaign.campaign_id] = {
        name: campaign.campaign,
        type: campaign.campaignType,
        targetingType: campaign.targeting_type,
        costType: campaign.cost_type,
      }
    })

    const extendedTargets = [];
    Object.keys(findDupsData.targets || {}).forEach((target) => {
      const total = {
        revenue: 0,
        cost: 0,
        impressions: 0,
        clicks: 0,
        orders: 0,
        units: 0,
        ntb_orders: 0,
        ntb_sales: 0,
        viewable_impressions: 0,
      }

      const children = [];
      findDupsData.targets[target].forEach((record) => {
        const campaignDetail = campaignsById[record.campaign_id] || {}
        const campaignType = campaignDetail.type || ''

        // Filter by ad type.
        if (filterValues && filterValues['dupResult'] && filterValues['dupResult']['campaignType']) {
          if (campaignType.toLowerCase() !== filterValues['dupResult']['campaignType'].value) {
            return
          }
        }

        total.revenue += parseFloat(record.revenue || 0)
        total.cost += parseFloat(record.cost || 0)
        total.impressions += parseInt(record.impressions || 0, 10)
        total.clicks += parseInt(record.clicks || 0, 10)
        total.orders += parseInt(record.orders || 0, 10)
        total.units += parseInt(record.units || 0, 10)
        total.ntb_orders += parseInt(record.ntb_orders || 0, 10)
        total.ntb_sales += parseFloat(record.ntb_sales || 0)
        total.viewable_impressions += parseInt(record.viewable_impressions || 0, 10)

        children.push({
          ...calcDerivedMetrics(record),
          campaignName: campaignDetail.name || '',
          campaignType,
          targetingType: campaignDetail.targetingType || '',
          costType: campaignDetail.costType || '',
        })
      })

      if (children.length) {
        extendedTargets.push({
          target_text: target,
          ...calcDerivedMetrics(total),
          children,
        })
      }
    })

    setTargets(extendedTargets)
  }, [findDupsData, campaignsWithHistory, filterValues])

  const [columns, columnSelection] = useMemo(() => {
    const availableColumns = campaignTableColumns.filter(c1 => bulkBidColumnList.find(c2 => c2.key === c1.key))
    return [
      [...initialColumns, ...availableColumns],
      availableColumns,
    ]
  }, [campaignTableColumns])

  const renderTarget = record => (
    <>
      <div className="table-col col-target" title={parseAsinTarget(record.target_text)}>
        <strong className="contents">
          { parseAsinTarget(record.target_text) }
        </strong>
      </div>
      {
        columnSelection.map(column => (
          <TableCell
            key={column.key}
            record={record}
            columnKey={column.key}
            columnSelection={columnSelection}
            currencySign={currencySign}
            currencyRate={currencyRate}
          />
        ))
      }
    </>
  )

  // Render aggregation row.
  const renderTotal = summary => (
    <>
      <div className="table-col col-target">Totals:</div>
      {
        columnSelection.map(column => (
          <TableCell
            key={column.key}
            record={summary}
            columnKey={column.key}
            columnSelection={columnSelection}
            currencySign={currencySign}
            currencyRate={currencyRate}
          />
        ))
      }
    </>
  )

  const getExportData = (exportableColumns, record) => (
    exportableColumns.map((column) => {
      if (column.key === 'target_text') {
        return parseAsinTarget(record.target_text)
      }
      return getExportValueForColumn(record, column.key, currencySign, currencyRate)
    })
  )


  return (
    <>
      <SortableTable
        columns={columns}
        defaultSort={['cost', 'desc']}
        sorter={tableSorter(['target_text'])}
        className="table-targets"
        records={targets || []}
        idField="target_text"
        searchFields={['target_text']}
        noCheckBox
        hasSticky
        hasDateRange
        hasLifetimeRange
        filterName="dupResult"
        columnEditorId="dupTargetResult"
        columnList={bulkBidColumnList}
        columnSelection={columnSelection}
        exportFileName="Find Duplicate Targets"
        getExportData={getExportData}
        renderRecord={renderTarget}
        renderTotal={renderTotal}
        onClickRecord={setSelectedTarget}
        onChangeDate={onChangeDate}
      />
      <DupTargetModal
        duplicatedTarget={selectedTarget}
        onClose={() => { setSelectedTarget(null) }}
      />
    </>
  )
}

export default DupTargetTable
